import { Icon, Label } from '@blueprintjs/core';
import BOSDateInput from 'Components/Generic/BOSDateInput';
import BOSInput from 'Components/Generic/BOSInput';
import AddressSelect from 'Components/Generic/BOSSelect/AddressSelect';
import { showSaveSuccessToast } from 'Components/Generic/BOSToasterUtil';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import DetailTable from 'Components/Generic/DetailPanel/DetailTable';
import EditableDetailPanel from 'Components/Generic/DetailPanel/EditableDetailPanel';
import styles from 'Components/Generic/Shipping/Shipping.module.css';
import ShippingSpeedRadioGroup from 'Components/Generic/Shipping/ShippingSpeedRadioGroup';
import GenericTableData from 'Data/GenericTableData';
import DataManager from 'Lib/DataManager';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { formatMoney } from 'Util';
import { formatDate } from 'Util/dateUtil';

const ShippingContext = createContext({});
ShippingContext.displayName = 'ShippingContext';

let DM = new DataManager();
export const ShippingStateProvider = ({
  request,
  transaction,
  editable = false,
  children,
}) => {
  const shippingDataRef = useRef(
    GenericTableData.getTableDataBus('SHIP', 'shipping'),
  );
  let ShippingData = shippingDataRef.current;

  const [initialShippingItem, setInitialShippingItem] = useState();

  const shippingItemId = request?.line_items?.length
    ? request.line_items[0].shipping_id
    : !!transaction?.id &&
      transaction?.shippings?.length &&
      transaction.shippings[0].id
    ? transaction.shippings[0].id
    : transaction?.line_items?.length && transaction?.line_items[0].shipping_id
    ? transaction.line_items[0].shipping_id
    : undefined;

  const {
    currentObject: shippingItem,
    hasBeenUpdated: shippingHasBeenUpdated,
    reset: resetShippingObject,
    updateField: updateShipping,
    updatedFields,
    doSave,
  } = useUpdatableObject(ShippingData.updateRecord, ShippingData.createRecord);

  const shippingItemRef = useRef();

  const [deliveryAddressOptions, setDeliveryAddressOptions] = useState();
  const [editState, setEditState] = useState(undefined);

  useEffect(() => {
    DM.add(
      ShippingData.registerListener('BLANK_RECORD_LOADED', onShippingLoaded),
      ShippingData,
    );
    DM.add(
      ShippingData.registerListener('RECORD_LOADED', onShippingLoaded),
      ShippingData,
    );
    DM.add(
      ShippingData.registerListener('RECORD_CREATED', shippingCreated),
      ShippingData,
    );
    DM.add(
      ShippingData.registerListener('RECORD_UPDATED', shippingUpdated),
      ShippingData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    setEditState(undefined);
  }, []);

  useEffect(() => {
    shippingItemRef.current = shippingItem;
  }, [shippingItem]);

  useEffect(() => {
    let si = !shippingItemId
      ? (request &&
          (request.requested_delivery_address_id || request.shipping_speed) && {
            ...(request.requested_delivery_address_id && {
              delivery_address_id: request.requested_delivery_address_id,
            }),
            ...(request.shipping_speed && {
              shipping_speed: request.shipping_speed,
            }),
            ...(request.shipping_amount && {
              amount: request.shipping_amount,
            }),
          }) ||
        (transaction?.shippings?.length &&
          transaction.shippings.map(
            ({ delivery_address_id, shipping_speed, amount }) => {
              return {
                delivery_address_id,
                shipping_speed,
                amount,
              };
            },
          )[0])
      : undefined;
    si && setInitialShippingItem(si);
  }, [request, transaction]);

  useEffect(() => {
    initialShippingItem &&
      !shippingItemId &&
      resetShippingObject(initialShippingItem);
  }, [initialShippingItem]);

  useEffect(() => {
    if (shippingItem) {
      if (
        transaction?.id &&
        shippingItem.parent_transaction_id !== transaction.id
      ) {
        updateShipping('parent_transaction_id', transaction.id);
      }
    }
  }, [transaction?.id, shippingItem]);

  useEffect(() => {
    if (shippingItemId !== undefined) {
      shippingItemId &&
        (!shippingItem || shippingItem.id !== shippingItemId) &&
        ShippingData.getRecord(shippingItemId);
      !shippingItem && shippingItemId === null && ShippingData.getBlank();
    } else {
      !shippingItem.id && !initialShippingItem && ShippingData.getBlank();
    }
  }, [shippingItemId]);

  const onShippingLoaded = (payload) => {
    if (payload) {
      resetShippingObject(payload);
    }
  };

  const resetShipping = () => {
    if (shippingItemId) {
      ShippingData.getRecord(shippingItemId);
    } else {
      initialShippingItem && resetShippingObject(initialShippingItem);
      !initialShippingItem && ShippingData.getBlank();
    }
  };

  const saveShipping = () => {
    if (
      (!shippingItem?.id && shippingItem?.delivery_address_id) ||
      shippingHasBeenUpdated
    )
      return doSave();
    return Promise.resolve();
  };

  const shippingCreated = (payload) => {
    if (payload) {
      resetShippingObject(payload);
    }
  };

  const shippingUpdated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Shipping Saved');
      resetShippingObject(payload);
      setEditState(false);
    }
  };

  const updateAmount = (newAmount) => {
    updateShipping('amount', newAmount);
  };

  const updateTracking = (newTracking) => {
    updateShipping('tracking_number', newTracking);
  };

  const updateDeliveryAddress = (newAddressId) => {
    updateShipping('delivery_address_id', newAddressId);
  };

  let deliveryAddressSelect = (
    <AddressSelect
      defaultSelectedId={shippingItem?.delivery_address_id}
      addressIdList={deliveryAddressOptions}
      onSelectAddress={(add) => updateDeliveryAddress(add?.id || null)}
    />
  );

  let getShippingInfoGroup = (inEditMode, includeOtherFields) => {
    return (
      <div className={styles.shipping_info_group}>
        {(shippingItem?.shipping_speed || inEditMode) && (
          <ShippingSpeedRadioGroup
            editable={inEditMode}
            selectedShippingSpeed={shippingItem?.shipping_speed}
            onSelectShipping={(e) =>
              updateShipping('shipping_speed', e.currentTarget.value)
            }
          />
        )}
        {includeOtherFields &&
          (inEditMode ||
            shippingItem?.tracking_number ||
            shippingItem?.expected_delivery_date) && (
            <div className={styles.tracking_and_date_group}>
              {(shippingItem?.tracking_number || inEditMode) && (
                <div className={styles.tracking_group}>
                  <Label className={styles.input_label}>Tracking</Label>
                  {inEditMode ? (
                    <BOSInput
                      showClear
                      value={shippingItem?.tracking_number}
                      onChange={updateTracking}
                    />
                  ) : (
                    shippingItem?.tracking_number || (
                      <span className={styles.no_content}>No tracking</span>
                    )
                  )}
                </div>
              )}
              {(shippingItem?.expected_delivery_date || inEditMode) && (
                <div className={styles.date_group}>
                  <Label className={styles.input_label}>Exp. Delivery</Label>
                  {inEditMode ? (
                    <BOSDateInput
                      value={shippingItem?.expected_delivery_date}
                      onChange={(date) =>
                        updateShipping('expected_delivery_date', date)
                      }
                    />
                  ) : (
                    formatDate(shippingItem?.expected_delivery_date)
                  )}
                </div>
              )}
            </div>
          )}
      </div>
    );
  };

  let getShippingCostInput = (inEditMode) => {
    return inEditMode ? (
      <BOSInput
        onChange={updateAmount}
        isMoneyInput
        value={shippingItem?.amount}
      />
    ) : (
      <div className={styles.cost_group}>
        <Icon size={12} icon='dollar' />
        {formatMoney(+shippingItem?.amount || 0)}
      </div>
    );
  };

  let getDetailData = (inEditState) => [
    // { label: 'id', value: shippingItem?.id || '' },
    // {
    //   label: 'delivery address',
    //   value: shippingItem?.delivery_address_id || '',
    // },
    // { label: 'Shipping Speed', value: getShippingInfoGroup(inEditState) },
    // { label: 'Cost', value: getShippingCostInput(inEditState) },
    {
      label: 'Tracking Number',
      value: inEditState
        ? shippingItem?.tracking_number
        : shippingItem?.tracking_number || (
            <span className={styles.no_content}>No tracking number</span>
          ),
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateShipping('tracking_number', newValue);
      },
    },
    {
      label: 'Expected Delivery Date',
      value: inEditState ? (
        <div style={{ minWidth: 140 }}>
          <BOSDateInput
            value={shippingItem?.expected_delivery_date}
            onChange={(date) => updateShipping('expected_delivery_date', date)}
          />
        </div>
      ) : (
        formatDate(shippingItem?.expected_delivery_date)
      ),
    },
  ];

  let getShippingInformationTable = (inEditState) => (
    <DetailTable data={getDetailData(inEditState)} editable={inEditState} />
  );

  let haveShippingFieldsChanged =
    Object.hasOwn(updatedFields, 'tracking_number') ||
    Object.hasOwn(updatedFields, 'expected_delivery_date');

  const undoButton = editable && haveShippingFieldsChanged && (
    <UndoButton clickHandler={resetShipping} />
  );

  const saveButton = editable && haveShippingFieldsChanged && (
    <SaveButton clickHandler={saveShipping} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  let getShippingInfoPanel = (props) => (
    <EditableDetailPanel
      title={'Shipping Information'}
      footer={footer}
      renderChild={getShippingInformationTable}
      editable={editable}
      parentEditState={editState}
      {...props}
    />
  );

  return (
    <ShippingContext.Provider
      value={{
        shippingItem,
        shippingItemRef,
        setDeliveryAddressOptions,
        deliveryAddressSelect,
        getShippingInfoGroup,
        getShippingCostInput,
        updateShipping,
        updatedFields,
        saveShipping,
        getShippingInfoPanel,
      }}>
      {children}
    </ShippingContext.Provider>
  );
};

ShippingStateProvider.propTypes = {
  request: PropTypes.shape({
    requested_delivery_address_id: PropTypes.number,
    shipping_speed: PropTypes.string,
    line_items: PropTypes.array,
  }),
  transaction: PropTypes.object,
  editable: PropTypes.bool,
  children: PropTypes.node,
};

export const useShippingContext = () => useContext(ShippingContext);
